import { supabase } from '@config/supabase';
import type { Session, AuthChangeEvent } from '@supabase/supabase-js';
import type { AuthUser } from './types';
import { UserPlan } from '@types';
import { createUserFromSession, fetchUserProfileFromDB, getInitialPlanFromStorage, getInitialUserFromStorage } from './utils';
import { authCache, USER_STATE_STORAGE_KEY, SESSION_HINT_KEY, SESSION_HINT_TTL, SESSION_CACHE_TTL, schedulePrefetch } from './authCache';

export function applySessionFromInitialize(
  session: Session,
  get: any,
  set: any,
  initStart: number
) {
  let initialUser: AuthUser | null = null;
  if (authCache.userStateCache && authCache.userStateCache.id === session.user.id) {
    initialUser = authCache.userStateCache;
  } else if (typeof window !== 'undefined') {
    try {
      const stored = sessionStorage.getItem(USER_STATE_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as AuthUser;
        if (parsed.id === session.user.id) {
          initialUser = parsed;
          authCache.userStateCache = parsed;
        }
      }
    } catch (_) {}
  }
  const userToSet = initialUser ?? createUserFromSession(session);
  set({
    user: userToSet,
    isAuthenticated: true,
    isLoading: false,
    session,
    isSuspended: userToSet.isSuspended || false,
  });
  if (!initialUser && userToSet.id) {
    authCache.userStateCache = userToSet;
  }
  console.log(`⏱️ [Auth] initialize (session applied) → isLoading=false, total ~${Math.round((typeof performance !== 'undefined' ? performance.now() : 0) - initStart)}ms`);
  schedulePrefetch();

  if (!initialUser) {
    fetchUserProfileFromDB(session.user.id)
      .then((fullUser) => {
        if (!fullUser) return;
        const current = get().user;
        if (current?.id !== fullUser.id) return;
        set({ user: fullUser, isSuspended: fullUser.isSuspended ?? false });
        authCache.userStateCache = fullUser;
        if (typeof window !== 'undefined') try { sessionStorage.setItem(USER_STATE_STORAGE_KEY, JSON.stringify(fullUser)); } catch (_) {}
      })
      .catch(() => {});
  }

  if (userToSet?.id && !authCache.suspensionListenerSetup) {
    authCache.suspensionListenerSetup = true;
    const suspensionChannel = supabase
      .channel(`user-suspension:${userToSet.id}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${userToSet.id}` }, async (payload) => {
        const updated = payload.new as Record<string, unknown>;
        if (updated.is_suspended === true) {
          authCache.sessionCache = { session: null, timestamp: Date.now() };
          await supabase.auth.signOut();
          set({ user: null, isAuthenticated: false, isLoading: false, session: null, error: 'ACCOUNT_SUSPENDED', isSuspended: true });
          import('@utils/sentry').then(({ clearUser }) => clearUser()).catch(() => {});
        } else {
          get().fetchUserProfile();
        }
      })
      .subscribe(() => {});
    (get() as any)._suspensionChannel = suspensionChannel;
  }
}

export function getHydratedAuthState(): Partial<any> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(USER_STATE_STORAGE_KEY);
    if (raw) {
      const user = JSON.parse(raw) as AuthUser;
      if (!user?.id) return {};
      const hintRaw = sessionStorage.getItem(SESSION_HINT_KEY);
      if (hintRaw) {
        const hint = JSON.parse(hintRaw) as { userId?: string; ts?: number };
        if (hint?.userId && hint.userId !== user.id) return {};
      }
      authCache.userStateCache = user;
      const planFromJwt = getInitialPlanFromStorage();
      const userToReturn = planFromJwt !== UserPlan.FREE ? { ...user, plan: planFromJwt } : user;
      return { user: userToReturn, isAuthenticated: true, isLoading: false };
    }
    const userFromToken = getInitialUserFromStorage();
    if (userFromToken) {
      authCache.userStateCache = userFromToken;
      return { user: userFromToken, isAuthenticated: true, isLoading: false };
    }
    return {};
  } catch {
    return {};
  }
}

export const initializeAuthStore = async (set: any, get: any) => {
  // Set up auth state change listener only once
  if (!authCache.authListenerSetup) {
    console.log('🔧 Setting up auth state change listener...');
    authCache.authListenerSetup = true;
    
    supabase.auth.onAuthStateChange(async (event: AuthChangeEvent, newSession: Session | null) => {
      try {
        console.log('🔄 Auth state changed:', event, {
          hasSession: !!newSession,
          userId: newSession?.user?.id,
          email: newSession?.user?.email,
        });

        // Update cache on any auth state change
        authCache.sessionCache = { session: newSession, timestamp: Date.now() };

        if (event === 'INITIAL_SESSION') {
          if (!newSession) {
            if (typeof window !== 'undefined') try { sessionStorage.removeItem(SESSION_HINT_KEY); } catch (_) {}
            set({ user: null, isAuthenticated: false, isLoading: false, session: null, isSuspended: false });
            return;
          }
          if (typeof window !== 'undefined') {
            try {
              sessionStorage.setItem(SESSION_HINT_KEY, JSON.stringify({ userId: newSession.user.id, ts: Date.now() }));
            } catch (_) {}
          }
          
          let userToSet: AuthUser;
          if (authCache.userStateCache && authCache.userStateCache.id === newSession.user.id) {
            userToSet = authCache.userStateCache;
          } else if (typeof window !== 'undefined') {
            try {
              const stored = sessionStorage.getItem(USER_STATE_STORAGE_KEY);
              const parsed = stored ? (JSON.parse(stored) as AuthUser) : null;
              if (parsed?.id === newSession.user.id) {
                userToSet = parsed;
                authCache.userStateCache = parsed;
              } else {
                userToSet = createUserFromSession(newSession);
              }
            } catch (_) {
              userToSet = createUserFromSession(newSession);
            }
          } else {
            userToSet = createUserFromSession(newSession);
          }
          
          const syncPlan = getInitialPlanFromStorage();
          if (syncPlan !== UserPlan.FREE) userToSet = { ...userToSet, plan: syncPlan };
          
          const current = get();
          if (current.user?.id === userToSet.id && current.user?.plan === userToSet.plan && current.isAuthenticated) {
            authCache.userStateCache = userToSet;
            if (typeof window !== 'undefined') {
              try { sessionStorage.setItem(SESSION_HINT_KEY, JSON.stringify({ userId: newSession.user.id, ts: Date.now() })); } catch (_) {}
            }
            authCache.sessionCache = { session: newSession, timestamp: Date.now() };
            set({ session: newSession });
            schedulePrefetch();
            fetchUserProfileFromDB(newSession.user.id)
              .then((fullUser) => {
                if (!fullUser) return;
                if (get().user?.id !== fullUser.id) return;
                set({ user: fullUser, session: newSession, isSuspended: fullUser.isSuspended ?? false });
                authCache.userStateCache = fullUser;
                if (typeof window !== 'undefined') try { sessionStorage.setItem(USER_STATE_STORAGE_KEY, JSON.stringify(fullUser)); } catch (_) {}
              })
              .catch(() => {});
            return;
          }
          set({
            user: userToSet,
            isAuthenticated: true,
            isLoading: false,
            session: newSession,
            error: null,
            isSuspended: userToSet.isSuspended ?? false,
          });
          authCache.userStateCache = userToSet;
          if (typeof window !== 'undefined') {
            try { sessionStorage.setItem(SESSION_HINT_KEY, JSON.stringify({ userId: newSession.user.id, ts: Date.now() })); } catch (_) {}
          }
          schedulePrefetch();
          fetchUserProfileFromDB(newSession.user.id)
            .then((fullUser) => {
              if (!fullUser) return;
              const current = get().user;
              if (current?.id !== fullUser.id) return;
              set({ user: fullUser, isSuspended: fullUser.isSuspended ?? false });
              authCache.userStateCache = fullUser;
              if (typeof window !== 'undefined') {
                try { sessionStorage.setItem(USER_STATE_STORAGE_KEY, JSON.stringify(fullUser)); } catch (e) {}
              }
            })
            .catch(() => {});
          return;
        }

        if (event === 'SIGNED_IN' && newSession) {
          console.log('✅ User signed in');
          console.log('🔍 Quick suspension check...');
          const suspensionCheckStart = Date.now();
          
          let isSuspended = false;
          let hasDownloadAccess = false;
          let suspensionCheck: any = null;
          let suspensionError: any = null;
          
          try {
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Suspension check timeout')), 5000)
            );

            const checkPromise = supabase
              .from('profiles')
              .select('is_suspended, suspension_download_enabled, deleted_at, mfa_enabled')
              .eq('id', newSession.user.id)
              .single();

            const result = await Promise.race([checkPromise, timeoutPromise]) as any;
            suspensionCheck = result?.data ?? null;
            suspensionError = result?.error ?? null;

            const suspensionCheckTime = Date.now() - suspensionCheckStart;
            console.log(`⏱️ Suspension check took ${suspensionCheckTime}ms`);
          } catch (error: any) {
            console.warn('⚠️ Suspension check timed out or failed:', error.message);
            suspensionError = error;
            suspensionCheck = null;
          }
          
          if (suspensionError) {
            isSuspended = false;
          } else {
            isSuspended = suspensionCheck?.is_suspended === true;
            hasDownloadAccess = suspensionCheck?.suspension_download_enabled === true;
            const isDeleted = suspensionCheck?.deleted_at !== null && suspensionCheck?.deleted_at !== undefined;
            
            if (isDeleted) {
              console.warn('⚠️ Account is soft-deleted - signing out immediately');
              import('@utils/sentry').then(({ captureException, setUser: setSentryUser, clearUser }) => {
                setSentryUser({ id: newSession.user.id, email: newSession.user.email || '' });
                const error = new Error('Soft-deleted account attempted login');
                error.name = 'SoftDeletedAccountLoginAttempt';
                captureException(error, {
                  source: 'auth',
                  loginMethod: 'oauth',
                  userId: newSession.user.id,
                  userEmail: newSession.user.email || '',
                  deleted_at: suspensionCheck?.deleted_at,
                });
                clearUser();
              }).catch(() => {});
              
              authCache.sessionCache = { session: null, timestamp: Date.now() };
              await supabase.auth.signOut();
              set({ 
                user: null, 
                isAuthenticated: false, 
                isLoading: false,
                session: null,
                error: 'ACCOUNT_DELETED',
                isSuspended: false
              });
              return;
            }
            
            if (isSuspended && !hasDownloadAccess) {
              console.warn('⚠️ Account is suspended - no download access, signing out immediately');
              import('@utils/sentry').then(({ captureException, setUser: setSentryUser, clearUser }) => {
                setSentryUser({ id: newSession.user.id, email: newSession.user.email || '' });
                const error = new Error('Suspended account attempted login');
                error.name = 'SuspendedAccountLoginAttempt';
                captureException(error, {
                  source: 'auth',
                  loginMethod: 'oauth',
                  userId: newSession.user.id,
                  userEmail: newSession.user.email || '',
                  hasDownloadAccess: false,
                });
                clearUser();
              }).catch(() => {});
              
              authCache.sessionCache = { session: null, timestamp: Date.now() };
              await supabase.auth.signOut();
              set({ 
                user: null, 
                isAuthenticated: false, 
                isLoading: false,
                session: null,
                error: 'ACCOUNT_SUSPENDED',
                isSuspended: true
              });
              return;
            }

            if (suspensionCheck?.mfa_enabled === true) {
              console.log('🔐 MFA enabled – skipping auth state update until MFA is completed');
              return;
            }
          }

          let mfaEnabled = suspensionCheck?.mfa_enabled === true;
          if (!mfaEnabled && suspensionError) {
            mfaEnabled = false;
          } else if (!mfaEnabled && suspensionError === null) {
            try {
              const { data: mfaRow } = await supabase.from('profiles').select('mfa_enabled').eq('id', newSession.user.id).single();
              mfaEnabled = mfaRow?.mfa_enabled === true;
            } catch {}
          }
          if (mfaEnabled) {
            console.log('🔐 MFA enabled – skipping auth state update until MFA is completed');
            return;
          }
          
          console.log('🔧 Creating basic user from session...');
          const basicUser = createUserFromSession(newSession);
          
          set({ 
            user: basicUser, 
            isAuthenticated: true, 
            isLoading: false,
            session: newSession,
            error: null,
            isSuspended: isSuspended,
          });
          
          // Background fetch storage limit/used
          (async () => {
            try {
              const { data: storageData } = await supabase
                .from('profiles')
                .select('storage_used, storage_limit')
                .eq('id', newSession.user.id)
                .single();
              
              if (storageData) {
                const currentUser = get().user;
                if (currentUser && currentUser.id === newSession.user.id) {
                  set({ 
                    user: {
                      ...currentUser,
                      storageUsed: storageData.storage_used ?? 0,
                      storageLimit: storageData.storage_limit ?? 1073741824,
                    }
                  });
                }
              }
            } catch (_) {}
          })();
          
          fetchUserProfileFromDB(newSession.user.id)
            .then(async (fullUser: AuthUser | null) => {
              if (!fullUser) {
                const { data: { user: authUser }, error: authError } = await supabase.auth.getUser(newSession.user.id);
                if (authError || !authUser) {
                  authCache.sessionCache = { session: null, timestamp: Date.now() };
                  await supabase.auth.signOut();
                  set({ user: null, isAuthenticated: false, isLoading: false, session: null, error: 'ACCOUNT_DELETED', isSuspended: false });
                  return;
                }
                await supabase.auth.signOut();
                set({ user: null, isAuthenticated: false, isLoading: false, session: null, error: 'ACCOUNT_DELETED', isSuspended: false });
                return;
              }
              
              if (fullUser.isSuspended) {
                const { data: profile } = await supabase
                  .from('profiles')
                  .select('suspension_download_enabled')
                  .eq('id', newSession.user.id)
                  .single();
                
                const hasDownload = profile?.suspension_download_enabled === true;
                if (hasDownload) {
                  set({ user: fullUser, isSuspended: true });
                } else {
                  import('@utils/sentry').then(({ captureException, setUser: setSentryUser, clearUser }) => {
                    setSentryUser({ id: newSession.user.id, email: newSession.user.email || '', username: fullUser.username });
                    const error = new Error('Suspended account attempted login');
                    error.name = 'SuspendedAccountLoginAttempt';
                    captureException(error, { source: 'auth', loginMethod: 'oauth', userId: newSession.user.id, userEmail: newSession.user.email || '', hasDownloadAccess: false });
                    clearUser();
                  }).catch(() => {});
                  
                  authCache.sessionCache = { session: null, timestamp: Date.now() };
                  await supabase.auth.signOut();
                  set({ user: null, isAuthenticated: false, isLoading: false, session: null, error: 'ACCOUNT_SUSPENDED', isSuspended: true });
                  return;
                }
              } else {
                set({ user: fullUser, isSuspended: false });
              }
              
              authCache.userStateCache = fullUser;
              if (typeof window !== 'undefined') {
                try { sessionStorage.setItem(USER_STATE_STORAGE_KEY, JSON.stringify(fullUser)); } catch (_) {}
              }
              
              if (!authCache.suspensionListenerSetup && fullUser.id) {
                authCache.suspensionListenerSetup = true;
                const suspensionChannel = supabase
                  .channel(`user-suspension:${fullUser.id}`)
                  .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${fullUser.id}` }, async (payload) => {
                    const updatedProfile = payload.new as { is_suspended?: boolean };
                    if (updatedProfile.is_suspended === true) {
                      authCache.sessionCache = { session: null, timestamp: Date.now() };
                      await supabase.auth.signOut();
                      set({ user: null, isAuthenticated: false, isLoading: false, session: null, error: 'ACCOUNT_SUSPENDED', isSuspended: true });
                      import('@utils/sentry').then(({ clearUser }) => clearUser()).catch(() => {});
                    } else if (updatedProfile.is_suspended === false) {
                      get().fetchUserProfile();
                    }
                  })
                  .subscribe(() => {});
                (get() as any)._suspensionChannel = suspensionChannel;
              }
              
              import('@utils/sentry').then(({ setUser: setSentryUser }) => {
                setSentryUser({ id: fullUser.id, email: fullUser.email, username: fullUser.username });
              }).catch(() => {});
            })
            .catch(async (error: any) => {
              if (error.message === 'ACCOUNT_DELETED' || error.message?.includes('ACCOUNT_DELETED')) {
                authCache.sessionCache = { session: null, timestamp: Date.now() };
                await supabase.auth.signOut();
                set({ user: null, isAuthenticated: false, isLoading: false, session: null, error: 'ACCOUNT_DELETED', isSuspended: false });
                return;
              }
            });
        } 
        
        else if (event === 'SIGNED_OUT') {
          console.log('🚪 User signed out');
          authCache.sessionCache = { session: null, timestamp: Date.now() };
          authCache.userStateCache = null;
          if (typeof window !== 'undefined') {
            try {
              sessionStorage.removeItem(USER_STATE_STORAGE_KEY);
              sessionStorage.removeItem(SESSION_HINT_KEY);
              sessionStorage.removeItem('fyle-files-cache');
              sessionStorage.removeItem('fyle-folders-cache');
              sessionStorage.removeItem('fyle-beatpacks-cache');
            } catch (_) {}
            import('../../stores/prefetchAppData').then((m) => m.clearPrefetchCache()).catch(() => {});
            import('../../stores/clearSessionScopedStores').then((m) => m.clearSessionScopedStores()).catch(() => {});
          }
          
          const suspensionChannel = (get() as any)._suspensionChannel;
          if (suspensionChannel) {
            supabase.removeChannel(suspensionChannel);
            (get() as any)._suspensionChannel = null;
            authCache.suspensionListenerSetup = false;
          }
          
          const current = get();
          set({ 
            user: null, 
            isAuthenticated: false, 
            isLoading: false,
            session: null,
            error: current.pendingMfaEmail ? current.error : null,
            isSuspended: false,
          });
          
          import('@utils/sentry').then(({ clearUser }) => clearUser()).catch(() => {});
        } 
        
        else if (event === 'TOKEN_REFRESHED' && newSession) {
          const currentSession = get().session;
          const didTokenChange =
            currentSession?.access_token !== newSession.access_token ||
            currentSession?.refresh_token !== newSession.refresh_token ||
            currentSession?.expires_at !== newSession.expires_at;
          if (!didTokenChange) return;
          console.log('🔄 Token refreshed silently');
          authCache.sessionCache = { session: newSession, timestamp: Date.now() };
          set({ session: newSession });
        } 
        
        else if (event === 'USER_UPDATED' && newSession) {
          const newEmail = newSession.user.email;
          if (newEmail) {
            try {
              const { error } = await supabase.from('profiles').update({ email: newEmail }).eq('id', newSession.user.id);
              if (!error) {
                const currentUser = get().user;
                if (currentUser && currentUser.email !== newEmail) {
                  get().fetchUserProfile();
                }
              }
            } catch (_) {}
          }
        }
      } catch (error: any) {
        console.error('❌ Error in auth state change handler:', error);
        if (get().isLoading) set({ isLoading: false });
      }
    });

    // Subscribes globally
    const { user } = get();
    if (user?.id) {
      const suspensionChannel = supabase
        .channel(`user-suspension:${user.id}`)
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${user.id}` }, async (payload) => {
          console.log('🚨 Real-time suspension update received:', payload.new);
          const updatedProfile = payload.new as { is_suspended?: boolean };
          
          if (updatedProfile.is_suspended === true) {
            authCache.sessionCache = { session: null, timestamp: Date.now() };
            await supabase.auth.signOut();
            set({ user: null, isAuthenticated: false, isLoading: false, session: null, error: 'ACCOUNT_SUSPENDED', isSuspended: true });
            import('@utils/sentry').then(({ clearUser }) => clearUser()).catch(() => {});
          } else if (updatedProfile.is_suspended === false) {
            get().fetchUserProfile();
          }
        })
        .subscribe(() => {});
      (get() as any)._suspensionChannel = suspensionChannel;
    }
  }

  console.log('🚀 Initializing auth...');
  const initStart = typeof performance !== 'undefined' ? performance.now() : 0;

  if (typeof window !== 'undefined') {
    try {
      const hintRaw = sessionStorage.getItem(SESSION_HINT_KEY);
      const userRaw = sessionStorage.getItem(USER_STATE_STORAGE_KEY);
      if (hintRaw && userRaw) {
        const hint = JSON.parse(hintRaw) as { userId?: string; ts?: number };
        const cachedUser = JSON.parse(userRaw) as AuthUser;
        if (hint?.userId && hint?.ts && cachedUser?.id === hint.userId && (Date.now() - hint.ts) < SESSION_HINT_TTL) {
          authCache.userStateCache = cachedUser;
          set({
            user: cachedUser,
            isAuthenticated: true,
            isLoading: false,
            session: null,
            isSuspended: cachedUser.isSuspended ?? false,
          });
          schedulePrefetch();
          
          supabase.auth.getSession().then(({ data: { session } }) => {
            if (!session) {
              authCache.sessionCache = { session: null, timestamp: Date.now() };
              sessionStorage.removeItem(SESSION_HINT_KEY);
              set({ user: null, isAuthenticated: false, isLoading: false, session: null, isSuspended: false });
              return;
            }
            authCache.sessionCache = { session, timestamp: Date.now() };
            sessionStorage.setItem(SESSION_HINT_KEY, JSON.stringify({ userId: session.user.id, ts: Date.now() }));
            set({ session });
            
            if (!authCache.suspensionListenerSetup && cachedUser?.id) {
              authCache.suspensionListenerSetup = true;
              const ch = supabase
                .channel(`user-suspension:${cachedUser.id}`)
                .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${cachedUser.id}` }, async (payload) => {
                  const p = (payload.new as any) as { is_suspended?: boolean };
                  if (p?.is_suspended === true) {
                    authCache.sessionCache = { session: null, timestamp: Date.now() };
                    await supabase.auth.signOut();
                    set({ user: null, isAuthenticated: false, isLoading: false, session: null, error: 'ACCOUNT_SUSPENDED', isSuspended: true });
                    import('@utils/sentry').then(({ clearUser }) => clearUser()).catch(() => {});
                  } else if (p?.is_suspended === false) {
                    get().fetchUserProfile();
                  }
                })
                .subscribe(() => {});
              (get() as any)._suspensionChannel = ch;
            }
          }).catch(() => {});
          return;
        }
      }
    } catch (_) {}
  }

  set({ error: null });

  try {
    const now = Date.now();

    if (get().user?.id && get().isAuthenticated) {
      authCache.sessionCache = null;
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) {
          authCache.sessionCache = { session, timestamp: Date.now() };
          if (typeof window !== 'undefined') try { sessionStorage.setItem(SESSION_HINT_KEY, JSON.stringify({ userId: session.user.id, ts: Date.now() })); } catch (_) {}
        } else {
          authCache.sessionCache = { session: null, timestamp: Date.now() };
          if (typeof window !== 'undefined') try { sessionStorage.removeItem(SESSION_HINT_KEY); } catch (_) {}
        }
      }).catch(() => {});
      return;
    }

    set({ isLoading: true });

    if (authCache.sessionCache && (now - authCache.sessionCache.timestamp) < SESSION_CACHE_TTL) {
      const session = authCache.sessionCache.session;
      if (!session) {
        const state = get();
        if (!state.isAuthenticated) set({ user: null, isAuthenticated: false, isLoading: false, session: null, isSuspended: false });
        return;
      }
      applySessionFromInitialize(session, get, set, initStart);
      return;
    }

    supabase.auth.getSession().then(({ data: { session: fetchedSession }, error: sessionError }) => {
      const state = get();
      if (state.isAuthenticated && state.user) {
        authCache.sessionCache = { session: state.session || fetchedSession, timestamp: Date.now() };
        if (fetchedSession && typeof window !== 'undefined') {
          try { sessionStorage.setItem(SESSION_HINT_KEY, JSON.stringify({ userId: fetchedSession.user.id, ts: Date.now() })); } catch (_) {}
        }
        return;
      }
      if (sessionError) {
        authCache.sessionCache = { session: null, timestamp: Date.now() };
        if (typeof window !== 'undefined') try { sessionStorage.removeItem(SESSION_HINT_KEY); } catch (_) {}
        set({ user: null, isAuthenticated: false, isLoading: false, session: null, isSuspended: false });
        return;
      }
      authCache.sessionCache = { session: fetchedSession, timestamp: Date.now() };
      if (fetchedSession && typeof window !== 'undefined') {
        try { sessionStorage.setItem(SESSION_HINT_KEY, JSON.stringify({ userId: fetchedSession.user.id, ts: Date.now() })); } catch (_) {}
      }
      if (!fetchedSession) {
        authCache.sessionCache = { session: null, timestamp: Date.now() };
        if (typeof window !== 'undefined') try { sessionStorage.removeItem(SESSION_HINT_KEY); } catch (_) {}
        return;
      }
      applySessionFromInitialize(fetchedSession, get, set, initStart);
    }).catch(() => {
      authCache.sessionCache = { session: null, timestamp: Date.now() };
      if (typeof window !== 'undefined') try { sessionStorage.removeItem(SESSION_HINT_KEY); } catch (_) {}
    });

  } catch (error: any) {
    console.error('❌ Auth initialization failed:', error);
    set({ user: null, isAuthenticated: false, isLoading: false, error: error.message, isSuspended: false });
  }
};
